import { useEffect, useState, useRef } from 'react'
import { gapi } from 'gapi-script'
import Address from 'services/Address'
import AlertDialog from 'components/AlertDialog'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { DateTime } from 'luxon'
import { Container, Button, Image, ButtonText } from './styles'
/* global google */

const ENV = import.meta.env.REACT_APP_ENVIRONMENT || 'production'

const CLIENT_ID = ENV === 'production'
  ? import.meta.env.REACT_APP_PROD_GOOGLE_CLIENT_ID
  : import.meta.env.REACT_APP_DEV_GOOGLE_CLIENT_ID

const API_KEY = ENV === 'production'
  ? import.meta.env.REACT_APP_PROD_CALENDAR_API_KEY
  : import.meta.env.REACT_APP_DEV_CALENDAR_API_KEY

const SCOPES = 'https://www.googleapis.com/auth/calendar'

export default function GoogleCalendarScheduler ({ schedules }) {
  const [loading, setLoading] = useState (false)
  const [isSignedIn, setIsSignedIn] = useState (false)
  const [errorMessage, setErrorMessage] = useState (null)
  const [showSuccessDialog, setShowSuccessDialog] = useState (false)
  const tokenClientRef = useRef (null)
  const gapiInitedRef = useRef (false)
  const gisInitedRef = useRef (false)

  useEffect (() => {
    const loadGapi = () => {
      gapi.load ('client', initializeGapiClient)
    }

    const loadGis = () => {
      const script = document.createElement ('script')
      script.src = 'https://accounts.google.com/gsi/client'
      script.onload = initializeGis
      document.head.appendChild (script)
    }

    const initializeGapiClient = async () => {
      await gapi.client.init ({
        apiKey: API_KEY,
        discoveryDocs: ['https://www.googleapis.com/discovery/v1/apis/calendar/v3/rest'],
      })
      gapiInitedRef.current = true
      maybeEnableButtons ()
    }

    const initializeGis = () => {
      tokenClientRef.current = google.accounts.oauth2.initTokenClient ({
        client_id: CLIENT_ID,
        scope: SCOPES,
        callback: (response) => {
          if (response.error !== undefined) {
            throw (response)
          }
          gapi.client.setToken (response)
          setIsSignedIn (true)
          if (createAfterAuth.current) {
            createGoogleCalendarEvent ()
            createAfterAuth.current = false
          }
        },
      })
      gisInitedRef.current = true
      maybeEnableButtons ()
    }

    const maybeEnableButtons = () => {
      if (gapiInitedRef.current && gisInitedRef.current) {
        const token = gapi.client.getToken ()
        if (token) setIsSignedIn (true)
      }
    }

    loadGapi ()
    loadGis ()
  }, [])

  const handleAuthClick = () => {
    if (isSignedIn) {
      createGoogleCalendarEvent ()
    } else {
      tokenClientRef.current.requestAccessToken ({ prompt: '' })
    }
  }

  async function createGoogleCalendarEvent () {
    setLoading (true)
    try {
      for (const schedule of schedules) {
        const scheduleStart = DateTime.fromFormat (`${schedule.date} ${schedule.time}`, 'yyyy-MM-dd H:mm')
        const scheduleEnd = scheduleStart.plus ({ minutes: schedule.duration })
        const professionalName = schedule.professional.nickname

        const event = {
          summary: `${schedule.service.name} em ${schedule.business.name} com ${professionalName}.`,
          location: Address.formatAddress (schedule.business.addresses[0]),
          start: {
            dateTime: scheduleStart.toISO (),
            timeZone: 'America/Sao_Paulo'
          },
          end: {
            dateTime: scheduleEnd.toISO (),
            timeZone: 'America/Sao_Paulo'
          }
        }

        const response = await gapi.client.calendar.events.insert ({
          calendarId: 'primary',
          resource: event
        })

        if (response.status === 200) {
          setShowSuccessDialog (true)
        } else {
          throw new Error ('Erro ao criar evento')
        }
      }
    } catch (error) {
      console.error ('Erro ao criar evento:', error)
      setErrorMessage ('Erro inesperado ao criar evento')
    } finally {
      setLoading (false)
    }
  }

  const createAfterAuth = useRef (false)

  const handleCreateAppointment = () => {
    setLoading (true)
    if (!isSignedIn) {
      createAfterAuth.current = true
      handleAuthClick ()
    } else {
      createGoogleCalendarEvent ()
    }
  }

  const toggleSuccessDialog = () => setShowSuccessDialog (!showSuccessDialog)

  return (
    <Container>
      <Button onClick={handleCreateAppointment} disabled={loading}>
        <Image
          src='https://img.icons8.com/color/48/000000/google-calendar.png'
          alt='Google Calendar'
        />
        <ButtonText>Marcar no Google Agenda</ButtonText>
        {loading && <Loading width={18} height={18} style={{ marginTop: 2 }}/>}
      </Button>
      <AlertDialog
        visible={!!errorMessage}
        title='Erro'
        message={errorMessage}
        confirmText='Ok'
        onConfirm={() => setErrorMessage (null)}
        onClose={() => setErrorMessage (null)}
      />
      <AlertDialog
        visible={showSuccessDialog}
        title='Sucesso'
        message='Agendamento adicionado com sucesso na sua agenda!'
        confirmText='Ok'
        onConfirm={toggleSuccessDialog}
        onClose={toggleSuccessDialog}
      />
    </Container>
  )
}
